import { useSessionKey } from "@auth/sessionStore";
import { perceptionKeys } from "@perception/perceptionQueries";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { visitsKeys } from "@visits/visitsQueries";

import {
  type CameraTransport,
  type FixtureBox,
  type Room,
  type RoomChange,
  type RoomKind,
  roomsClient,
} from "./roomsClient";

const roomsKeys = {
  all: ["rooms"] as const,
  list: (sessionKey: string, active?: boolean) =>
    ["rooms", sessionKey, active ?? "all"] as const,
  baseline: (sessionKey: string, roomId: string) =>
    ["rooms", sessionKey, "baseline", roomId] as const,
};

/** Shared so suspense and non-suspense readers hit the same cache entry. */
const roomsListQueryOptions = (sessionKey: string, active?: boolean) => ({
  queryKey: roomsKeys.list(sessionKey, active),
  queryFn: () =>
    roomsClient.list(active === undefined ? undefined : { active }),
});

export const useRoomsList = (sessionKey: string, active?: boolean) =>
  useSuspenseQuery(roomsListQueryOptions(sessionKey, active));

export const useRoomsListQuery = (
  sessionKey: string,
  active: boolean | undefined,
  enabled: boolean,
) => useQuery({ ...roomsListQueryOptions(sessionKey, active), enabled });

export const useCreateRoom = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, kind }: { name: string; kind: RoomKind }) =>
      roomsClient.create(name, kind),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: roomsKeys.all }),
    meta: { handled: true },
  });
};

/** Swaps the saved room into the cached lists rather than refetching them; the phases list and the room's ground-truth buttons carry its kind. */
export const useUpdateRoom = () => {
  const queryClient = useQueryClient();
  const sessionKey = useSessionKey();
  return useMutation({
    mutationFn: ({
      roomUuid,
      change,
    }: {
      roomUuid: string;
      change: RoomChange;
    }) => roomsClient.update(roomUuid, change),
    onSuccess: (room) => {
      queryClient.setQueriesData<Room[]>(
        { queryKey: roomsKeys.all },
        (rooms) =>
          Array.isArray(rooms)
            ? rooms.map((r) => (r.id === room.id ? room : r))
            : rooms,
      );
      void queryClient.invalidateQueries({
        queryKey: perceptionKeys.phases(sessionKey),
      });
      void queryClient.invalidateQueries({
        queryKey: perceptionKeys.groundTruth(sessionKey, room.id),
      });
    },
  });
};

/** The caller shows its own error, so the app-wide write notice stays out of it. */
export const useSetRoomCamera = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      roomUuid,
      transport,
      fixtures,
    }: {
      roomUuid: string;
      transport: CameraTransport;
      fixtures: readonly FixtureBox[] | null;
    }) => roomsClient.setCamera(roomUuid, transport, fixtures),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: roomsKeys.all }),
    meta: { handled: true },
  });
};

/** Refetched each time it is enabled: brian's presigned URL lasts 5 minutes. */
export const useRoomBaseline = (
  sessionKey: string,
  roomId: string,
  enabled: boolean,
) =>
  useQuery({
    queryKey: roomsKeys.baseline(sessionKey, roomId),
    queryFn: () => roomsClient.baseline(roomId),
    enabled,
    staleTime: 0,
  });

export const useSetRoomBaseline = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      roomUuid,
      stillReference,
    }: {
      roomUuid: string;
      stillReference: string | null;
    }) => roomsClient.setBaseline(roomUuid, stillReference),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: roomsKeys.all }),
    meta: { handled: true },
  });
};

export const useClearRoomBaseline = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roomUuid: string) => roomsClient.clearBaseline(roomUuid),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: roomsKeys.all }),
    meta: { handled: true },
  });
};

interface RoomVisit {
  roomUuid: string;
  visitUuid: string;
}

/** Settles either way: a 409 means the visits we showed are stale. */
const useRoomVisitMutation = <V extends RoomVisit>(
  mutationFn: (vars: V) => Promise<void>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: visitsKeys.all }),
    meta: { handled: true },
  });
};

export const useConfirmRoomVisit = () =>
  useRoomVisitMutation((vars: RoomVisit & { includeHousehold: boolean }) =>
    roomsClient.confirmVisit(vars),
  );

export const useReleaseRoomVisit = () =>
  useRoomVisitMutation(({ roomUuid, visitUuid }) =>
    roomsClient.releaseVisit(roomUuid, visitUuid),
  );
